import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
   ChevronLeft, MapPin, Info, Film, Users, Camera,
   AlertCircle, FileText, Calendar, Clock, ChevronRight, PlayCircle, Phone, Star, CheckCircle, Shield
} from 'lucide-react';

export default function ShowDetails() {
   const { id } = useParams();
   const navigate = useNavigate();

   const [showData, setShowData] = useState(null);
   const [loading, setLoading] = useState(true);
   const [isScrolled, setIsScrolled] = useState(false);
   const inlineBookingRef = useRef(null);

   useEffect(() => {
      window.scrollTo(0, 0);

      const fetchShowDetails = async () => {
         try {
            const headers = {
               'Client-ID': 'lokanatya_client',
               'Client-Secret': 'super_secret_key_123'
            };
            const response = await axios.get(`/api/get_product_id/${id}`, { headers });
            if (response.data && response.data.status) {
               setShowData(response.data.data);
            }
         } catch (error) {
            console.error('Error fetching show details:', error);
         } finally {
            setLoading(false);
         }
      };

      fetchShowDetails();
   }, [id]);

   useEffect(() => {
      const handleScroll = () => {
         if (inlineBookingRef.current) {
            const rect = inlineBookingRef.current.getBoundingClientRect();
            setIsScrolled(rect.bottom < 0);
         }
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
   }, []);

   if (loading) {
      return (
         <div className="bg-[#fcfcfc] min-h-screen pb-36 lg:pb-16 animate-pulse">
            {/* Top Banner Skeleton */}
            <div className="w-full h-[250px] lg:h-[400px] bg-gray-200"></div>
            
            {/* Main Content Area Skeleton */}
            <div className="max-w-7xl mx-auto relative z-20 flex flex-col lg:flex-row gap-6 md:gap-8 px-2 sm:px-6 lg:px-8 -mt-6 lg:mt-6 bg-[#fcfcfc] lg:bg-transparent pt-8 lg:pt-0 rounded-t-3xl lg:rounded-none">
               
               {/* Left Column Skeleton */}
               <div className="w-full lg:w-[65%] flex flex-col gap-6">
                  <div className="h-10 bg-gray-200 w-3/4 rounded mb-2"></div>
                  <div className="h-5 bg-gray-200 w-1/2 rounded mb-6"></div>
                  
                  <div className="bg-white rounded-[1.5rem] p-5 shadow-xs border border-gray-100 h-[150px]"></div>
                  <div className="bg-white rounded-[1.5rem] p-5 shadow-xs border border-gray-100 h-[300px]"></div>
               </div>

               {/* Right Column Skeleton */}
               <div className="w-full lg:w-[35%] hidden lg:block">
                  <div className="bg-white rounded-[2rem] p-6 shadow-xl border border-gray-100 sticky top-24 h-[350px]"></div>
               </div>
            </div>
         </div>
      );
   }

   if (!showData) {
      return <div className="min-h-screen flex items-center justify-center bg-[#fcfcfc] text-xl font-bold">Show not found.</div>;
   }

   const formattedDate = showData.jatra_date
      ? new Date(showData.jatra_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : 'N/A';

   const canSelectSeat = Number(showData.status) === 1 && Number(showData.isvisible) === 0;

   return (
      <div className="bg-[#fcfcfc] min-h-screen pb-16 lg:pb-8">

         {/* Hero Banner */}
         <div className="relative w-full aspect-square md:aspect-video lg:h-[500px]">
            <img
               src={showData.image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"}
               alt={showData.jatra_name}
               className="w-full h-full object-cover"
               onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/30 pointer-events-none"></div>

            <div className="absolute top-4 left-3 right-3 sm:top-6 sm:left-4 sm:right-4 flex justify-between z-10">
               <button
                  onClick={() => navigate(-1)}
                  className="bg-black/40 hover:bg-black/60 text-white rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2 flex items-center gap-1.5 backdrop-blur-md transition font-semibold text-xs sm:text-sm border border-white/20 shadow-md"
               >
                  <ChevronLeft size={16} strokeWidth={2.5} /> Back
               </button>
               {showData.youtube_link ? (
                  <a href={showData.youtube_link} target="_blank" rel="noreferrer" className="bg-black/40 hover:bg-black/60 text-white rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2 flex items-center gap-1.5 backdrop-blur-md transition font-semibold text-xs sm:text-sm border border-white/20 shadow-md">
                     <PlayCircle size={16} className="text-[#e33535] fill-[#e33535]" /> Trailer
                  </a>
               ) : (
                  <button className="bg-black/40 hover:bg-black/60 text-white rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2 flex items-center gap-1.5 backdrop-blur-md transition font-semibold text-xs sm:text-sm border border-white/20 opacity-50 cursor-not-allowed">
                     <PlayCircle size={16} className="text-[#e33535] fill-[#e33535]" /> Trailer
                  </button>
               )}
            </div>
         </div>

         {/* Main Content Area */}
         <div className="max-w-7xl mx-auto relative z-20 flex flex-col lg:flex-row lg:items-start gap-4 sm:gap-6 md:gap-8 px-2 sm:px-6 lg:px-8 -mt-6 lg:mt-6 bg-[#fcfcfc] lg:bg-transparent pt-6 lg:pt-0 rounded-t-3xl lg:rounded-none">

            {/* Left Column (Details) */}
            <div className="w-full lg:w-[65%] flex flex-col gap-4 sm:gap-5">

               {/* Header Info (Pills, Title, Date, Location) - Mobile Only */}
               <div className="mb-1 lg:hidden px-2">
                  <h1 className="text-xl sm:text-2xl md:text-4xl font-extrabold text-gray-900 mb-3 tracking-tight leading-snug">{showData.jatra_name}</h1>

                  <div className="flex flex-col gap-2.5 text-xs sm:text-sm text-gray-700">
                     <div className="flex items-center gap-2.5">
                        <Calendar size={16} className="text-red-500 shrink-0" />
                        <span className="font-medium">{formattedDate} &bull; {showData.jatra_time || '11:00 PM'} - 5:00 AM</span>
                     </div>
                     <div className="flex items-center gap-2.5">
                        <MapPin size={16} className="text-blue-500 shrink-0" />
                        <span className="font-medium leading-relaxed">{showData.jatra_location || 'Odisha'}</span>
                     </div>
                  </div>
               </div>

               {/* Inline Booking Section - Mobile Only */}
               <div ref={inlineBookingRef} className="lg:hidden bg-[#151515] rounded-2xl p-4 flex items-center justify-between shadow-md mb-1 mx-1">
                  <div className="flex flex-col pl-1">
                     <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">FROM</span>
                     <span className="text-2xl font-extrabold text-[#e33535] leading-none tracking-tight">₹ {showData.ticket_price || 200}</span>
                  </div>
                  <div className="flex gap-2 items-center min-w-0">
                     {canSelectSeat ? (
                        <Link to={`/shows/${id}/seats`} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 sm:px-7 rounded-xl shadow-md transition flex items-center gap-2 text-xs sm:text-sm">
                           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>
                           <span className="truncate">Select Your Seat</span>
                        </Link>
                     ) : (
                        <button disabled className="bg-gray-400 text-white font-bold py-2.5 px-5 sm:px-7 rounded-xl shadow-xs cursor-not-allowed flex items-center justify-center gap-2 text-xs sm:text-sm">
                           <span className="truncate">Coming Soon</span>
                        </button>
                     )}
                  </div>
               </div>

               {/* Location & Time Card */}
               <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100 relative">
                  <div className="flex items-center justify-between mb-4">
                     <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                           <MapPin size={15} className="text-[#e33535]" />
                        </div>
                        <h2 className="text-base sm:text-lg font-bold text-gray-900">Location & Time</h2>
                     </div>
                     <a 
                        href={showData.latitude && showData.longitude ? `https://www.google.com/maps/dir/?api=1&destination=${showData.latitude},${showData.longitude}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(showData.jatra_location || '')}`}
                        target="_blank" 
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1d4ed8] bg-blue-50 hover:bg-[#1d4ed8] hover:text-white px-3 py-1.5 rounded-full transition-colors shrink-0 border border-blue-100 shadow-2xs"
                     >
                        <MapPin size={13} /> Get Direction
                     </a>
                  </div>
                  <div className="space-y-3">
                     <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">COMMITTEE</p>
                        <h3 className="text-sm font-bold text-gray-900">{showData.cat_name || 'Lokanatya Committee'}</h3>
                     </div>
                     <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">DATE</p>
                        <p className="text-sm font-bold text-gray-900">Friday, {formattedDate}</p>
                     </div>
                     <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">LOCATION</p>
                        <p className="text-sm font-bold text-gray-900 leading-snug">
                           {showData.jatra_location || 'Odisha'}
                        </p>
                     </div>
                  </div>
               </div>

               {/* About the Jatra Card */}
               <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100">
                  <div className="flex items-center gap-2.5 mb-4">
                     <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <Info size={15} className="text-[#e33535]" />
                     </div>
                     <h2 className="text-base sm:text-lg font-bold text-gray-900">About the Jatra</h2>
                  </div>
                  <div
                     className="text-xs sm:text-sm text-gray-600 leading-relaxed max-w-none prose prose-sm font-medium"
                     dangerouslySetInnerHTML={{ __html: showData.detail || '2026-27 season brings an incredible story filled with drama, emotion, and traditional performances.' }}
                  />
               </div>

               {/* Show Guide Card */}
               <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100">
                  <div className="flex items-center gap-2.5 mb-4">
                     <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <Star size={15} className="text-[#e33535]" />
                     </div>
                     <h2 className="text-base sm:text-lg font-bold text-gray-900">Show Guide</h2>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
                     <div className="border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center bg-gray-50/50">
                        <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">LANGUAGE</p>
                        <p className="text-xs sm:text-sm font-bold text-gray-900">Odia</p>
                     </div>
                     <div className="border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center bg-gray-50/50">
                        <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">DURATION</p>
                        <p className="text-xs sm:text-sm font-bold text-gray-900 text-center">{showData.duration || '2h 45m'}</p>
                     </div>
                     <div className="border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center bg-gray-50/50">
                        <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">AUDIENCE</p>
                        <p className="text-xs sm:text-sm font-bold text-gray-900">All Ages</p>
                     </div>
                  </div>
               </div>

               {/* Creative Credits Card */}
               <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100">
                  <div className="flex items-center gap-2.5 mb-4">
                     <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <Star size={15} className="text-[#e33535]" />
                     </div>
                     <h2 className="text-base sm:text-lg font-bold text-gray-900">Creative Credits</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-3">
                     <div className="flex flex-col items-center p-3 bg-gray-50/60 rounded-xl border border-gray-100">
                        <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Story</span>
                        <span className="text-xs sm:text-sm font-bold text-gray-900 text-center">{showData.story_writer || 'Bibhuti Pattnaik'}</span>
                     </div>
                     <div className="flex flex-col items-center p-3 bg-gray-50/60 rounded-xl border border-gray-100">
                        <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Direction</span>
                        <span className="text-xs sm:text-sm font-bold text-gray-900 text-center">{showData.direction || 'Sanjay Mohanty'}</span>
                     </div>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-gray-50/60 rounded-xl border border-gray-100 w-full mt-2">
                     <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Music</span>
                     <span className="text-xs sm:text-sm font-bold text-gray-900 text-center">{showData.music || 'Papu Pom Pom'}</span>
                  </div>
               </div>

               {/* Cast & Crew Card */}
               <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100">
                  <div className="flex items-center gap-2.5 mb-4">
                     <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <Users size={15} className="text-[#e33535]" />
                     </div>
                     <h2 className="text-base sm:text-lg font-bold text-gray-900">Cast & Crew</h2>
                  </div>
                  <div className="flex gap-4 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                     {(showData.actors ? (Array.isArray(showData.actors) ? showData.actors : JSON.parse(showData.actors || '[]')) : [1, 2, 3, 4, 5]).map((actor, idx) => (
                        <div key={idx} className="flex flex-col items-center gap-1.5 shrink-0">
                           <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-200 overflow-hidden shadow-xs border border-gray-100">
                              <img src={actor.image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"} alt="Actor" className="w-full h-full object-cover" onError={(e) => e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"} />
                           </div>
                           <span className="text-[11px] sm:text-xs font-bold text-gray-800">{actor.name || `Actor ${idx + 1}`}</span>
                        </div>
                     ))}
                  </div>
               </div>

               {showData.youtube_iframe && (
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-xs border border-gray-100 relative overflow-hidden group">
                     <div className="flex items-center gap-2.5 mb-4">
                        <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                           <Film size={15} className="text-red-600" />
                        </div>
                        <h2 className="text-base sm:text-lg font-bold text-gray-900">Trailer & Promos</h2>
                     </div>
                     <div className="aspect-video w-full rounded-xl overflow-hidden shadow-xs" dangerouslySetInnerHTML={{ __html: showData.youtube_iframe }}></div>
                  </div>
               )}

               {/* Good to Know Card */}
               <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100 mb-2">
                  <div className="flex items-center gap-2.5 mb-4">
                     <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <CheckCircle size={15} className="text-[#e33535]" />
                     </div>
                     <h2 className="text-base sm:text-lg font-bold text-gray-900">Good to Know</h2>
                  </div>
                  <ul className="space-y-3">
                     {[
                        "Gates open ~45 mins before show time.",
                        "Tickets are generally non-refundable.",
                        "Seats are allotted as per section.",
                        "Outside food is restricted.",
                        "Please follow on-ground instructions."
                     ].map((text, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-gray-700 font-medium">
                           <div className="w-4 h-4 rounded-full bg-[#e33535] text-white flex items-center justify-center text-[9px] font-bold mt-0.5 shrink-0">{i + 1}</div>
                           <span>{text}</span>
                        </li>
                      ))}
                  </ul>
               </div>

            </div>

            {/* Desktop Right Sidebar Card */}
            <div className="hidden lg:block w-full lg:w-[35%] sticky top-24 h-fit">
               <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                  <h1 className="text-xl font-bold text-gray-900 mb-4 leading-tight">{showData.jatra_name}</h1>

                  <div className="flex items-center justify-between mb-6">
                     <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">SEAT PRICE STARTS FROM</p>
                     <h2 className="text-3xl font-extrabold text-[#e33535]">₹ {showData.ticket_price || 200}</h2>
                  </div>

                  <div className="space-y-4 mb-8">
                     <div className="flex gap-3 items-start">
                        <Calendar size={20} className="text-[#e33535] shrink-0 mt-0.5" />
                        <div>
                           <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Date</span>
                           <span className="text-sm font-bold text-gray-900">{formattedDate}</span>
                        </div>
                     </div>
                     <div className="flex gap-3 items-start">
                        <Clock size={20} className="text-[#e33535] shrink-0 mt-0.5" />
                        <div>
                           <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Time</span>
                           <span className="text-sm font-bold text-gray-900">{showData.jatra_time || 'N/A'}</span>
                        </div>
                     </div>
                     <div className="flex gap-3 items-start">
                        <MapPin size={20} className="text-[#e33535] shrink-0 mt-0.5" />
                        <div>
                           <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">LOCATION</p>
                           <div className="flex items-center justify-between gap-4">
                              <p className="text-sm font-bold text-gray-900 leading-snug">
                                 {showData.cat_name}, {showData.jatra_location || 'Odisha'}
                              </p>
                              <a 
                                 href={showData.latitude && showData.longitude ? `https://www.google.com/maps/dir/?api=1&destination=${showData.latitude},${showData.longitude}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(showData.jatra_location || '')}`}
                                 target="_blank" 
                                 rel="noreferrer"
                                 className="inline-flex items-center gap-1.5 text-[10px] md:text-xs font-bold text-[#1d4ed8] bg-blue-50 hover:bg-[#1d4ed8] hover:text-white px-2.5 py-1 md:px-3 md:py-1.5 rounded-full transition-colors shrink-0 shadow-sm border border-blue-100"
                              >
                                 <MapPin size={12} className="md:w-3.5 md:h-3.5" /> Get Direction
                              </a>
                           </div>
                        </div>
                     </div>
                  </div>

                  {canSelectSeat ? (
                     <Link to={`/shows/${id}/seats`} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl shadow-md transition text-lg mb-3 flex items-center justify-center gap-2">
                        Select Your Seat
                     </Link>
                  ) : (
                     <button disabled className="w-full bg-gray-400 text-white font-bold py-3 px-4 rounded-xl shadow-md cursor-not-allowed text-lg mb-3 flex items-center justify-center gap-2">
                        Coming Soon
                     </button>
                  )}

                  <p className="text-[10px] text-gray-400 text-center mt-6">
                     Taxes & convenience fees may apply on checkout
                  </p>
               </div>
            </div>

         </div>

         {/* Mobile Sticky Bottom Booking Bar */}
         <div className={`lg:hidden fixed bottom-0 left-0 right-0 bg-[#151515] border-t border-white/5 px-4 py-3 shadow-[0_-10px_40px_rgba(0,0,0,0.2)] z-[50] flex items-center justify-between rounded-t-3xl pb-safe transition-all duration-300 ease-in-out ${isScrolled ? 'translate-y-0 opacity-100' : 'translate-y-[150%] opacity-0 pointer-events-none'}`}>
            <div className="flex flex-col pl-2">
               <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">FROM</span>
               <span className="text-2xl font-extrabold text-[#e33535] leading-none">₹ {showData.ticket_price || 200}</span>
            </div>
            {canSelectSeat ? (
               <Link to={`/shows/${id}/seats`} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-8 rounded-2xl shadow-lg transition flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>
                  Select Your Seat
               </Link>
            ) : (
               <button disabled className="bg-gray-400 text-white font-bold py-3.5 px-8 rounded-2xl shadow-md cursor-not-allowed flex items-center gap-2">
                  Coming Soon
               </button>
            )}
         </div>

         {/* Floating Phone FAB when scrolled */}
         <a href="tel:+918917493408" title="Call Us: +91 8917493408" className={`lg:hidden fixed right-4 z-[45] bg-blue-600 hover:bg-blue-700 text-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 ease-in-out ${isScrolled ? 'bottom-[90px] opacity-100' : 'bottom-0 opacity-0 pointer-events-none'}`}>
            <Phone size={24} className="fill-current" />
         </a>

      </div>
   );
}
